import type {
  User,
  RegisterRequest,
  LoginRequest,
  VerifyOTPRequest,
  AuthResponse,
  Bookmark,
  ReadingProgress,
  MemorizationData,
  MemorizationTest,
  TestVerseAttempt,
  Note,
  Playlist,
  CreatePlaylistInput,
  UpdatePlaylistInput,
  SharedPlaylistSummary,
  GameType,
  GameSource,
  GameSession,
  RecordGameSessionResponse,
} from '../types'
import { fetchAuthSession } from 'aws-amplify/auth'
import i18n from '../i18n/config'
import {
  clearActivity,
  isInactivityExpired,
  recordActivity,
} from './sessionActivity'

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001'

// Custom error class
export class ApiError extends Error {
  constructor(
    public code: string,
    message: string,
    public statusCode: number
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Custom event broadcast when the session is no longer recoverable (no
// refresh token, or the refresh request itself failed). The AuthProvider
// listens for it to clear in-memory user state and bounce the user to /auth.
// Using a window event keeps lib/api.ts decoupled from the React tree.
export const AUTH_EXPIRED_EVENT = 'qlearn:auth-expired'

function expireSession(): ApiError {
  clearInFlightGets()
  tokenStorage.clearTokens()
  // Defer the dispatch a tick so the throw below propagates first — components
  // that catch the ApiError get to render their fallback before navigation.
  if (typeof window !== 'undefined') {
    queueMicrotask(() => {
      window.dispatchEvent(new CustomEvent(AUTH_EXPIRED_EVENT))
    })
  }
  // Use the client-side i18n bundle for the message so it lands in the
  // user's current locale (the backend never sees this code — it's locally
  // synthesized when refresh fails, so api.ts's usual code→translation
  // pipeline doesn't run for it).
  const msg = i18n.exists('errors.AUTH_EXPIRED') ? i18n.t('errors.AUTH_EXPIRED') : 'Session expired'
  return new ApiError('AUTH_EXPIRED', msg, 401)
}

// Synthesized locally when fetch itself rejects (no HTTP response at all):
// airplane mode, dropped Wi-Fi, DNS failure. Distinct code so auth code can
// tell "the network is gone" apart from "the server said no".
function offlineError(): ApiError {
  const msg = i18n.exists('errors.NETWORK_OFFLINE')
    ? i18n.t('errors.NETWORK_OFFLINE')
    : "You're offline. Check your connection and try again."
  return new ApiError('NETWORK_OFFLINE', msg, 0)
}

export function isOfflineError(err: unknown): boolean {
  return err instanceof ApiError && err.code === 'NETWORK_OFFLINE'
}

// fetch() rejects with a TypeError when the request never got a response.
// Wrap that as NETWORK_OFFLINE, but let AbortError through untouched: the
// GET hooks abort on unmount/param change and that must not surface as an
// offline condition (or poison retry logic).
async function fetchOrThrowOffline(input: string, init?: RequestInit): Promise<Response> {
  try {
    return await fetch(input, init)
  } catch (err) {
    if (err instanceof DOMException && err.name === 'AbortError') throw err
    throw offlineError()
  }
}

// Token storage
export const tokenStorage = {
  getToken(): string | null {
    return localStorage.getItem('accessToken')
  },
  setToken(token: string): void {
    localStorage.setItem('accessToken', token)
  },
  getIdToken(): string | null {
    return localStorage.getItem('idToken')
  },
  setIdToken(token: string): void {
    localStorage.setItem('idToken', token)
  },
  getRefreshToken(): string | null {
    return localStorage.getItem('refreshToken')
  },
  setRefreshToken(token: string): void {
    localStorage.setItem('refreshToken', token)
  },
  clearTokens(): void {
    localStorage.removeItem('accessToken')
    localStorage.removeItem('idToken')
    localStorage.removeItem('refreshToken')
  },
}

// Single-flight token refresh.
//
// When the id token expires, the several authenticated requests the app fires
// in parallel (profile, playlists, progress, …) ALL get a 401 within the same
// instant. Without coordination each one would POST /auth/refresh on its own —
// a burst Cognito can throttle, and one that races the others while storing the
// new tokens. Any single failure in that burst calls expireSession(), which
// bounces an actively-using user straight to the login screen ("not keeping me
// logged in while I'm using it"). So we share ONE in-flight refresh: the first
// 401 starts it, every other 401 awaits the same promise, and only a genuine
// refresh failure expires the session. (Mirrors the GET dedupe below.)
let refreshInFlight: Promise<boolean> | null = null

async function refreshSession(): Promise<boolean> {
  if (refreshInFlight) return refreshInFlight
  refreshInFlight = (async () => {
    let sawOffline = false
    // Path 1 — custom OTP login stores a refresh token in localStorage; use
    // the backend /auth/refresh endpoint for it.
    const refreshToken = tokenStorage.getRefreshToken()
    if (refreshToken) {
      try {
        const res = await fetchOrThrowOffline(`${API_BASE_URL}/auth/refresh`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refreshToken }),
        })
        if (res.ok) {
          const data = await res.json()
          // Backend may return { accessToken, idToken } flat or { tokens:{…} }.
          const newAccess = data?.tokens?.accessToken ?? data?.accessToken
          const newIdToken = data?.tokens?.idToken ?? data?.idToken
          if (newAccess && newIdToken) {
            tokenStorage.setToken(newAccess)
            tokenStorage.setIdToken(newIdToken)
            return true
          }
        }
      } catch (err) {
        if (isOfflineError(err)) sawOffline = true
        // fall through to the Amplify path
      }
    }

    // Path 2 — Amplify-managed sessions (Google / hosted-UI sign-in) keep the
    // refresh token in Amplify's OWN storage, never in tokenStorage, so the
    // path above has nothing to send. Amplify owns the refresh token — ask it
    // to mint fresh tokens. THIS is what was missing: OAuth sessions had no
    // stored refresh token, so every hourly id-token expiry fell straight
    // through to logout. `forceRefresh` makes Amplify use its refresh token
    // even when it thinks the cached tokens are still valid.
    try {
      const session = await fetchAuthSession({ forceRefresh: true })
      const newAccess = session.tokens?.accessToken?.toString()
      const newIdToken = session.tokens?.idToken?.toString()
      if (newAccess && newIdToken) {
        tokenStorage.setToken(newAccess)
        tokenStorage.setIdToken(newIdToken)
        return true
      }
    } catch {
      // Amplify throws opaque errors; navigator.onLine is the only signal left.
      if (typeof navigator !== 'undefined' && navigator.onLine === false) sawOffline = true
      // fall through to failure
    }
    if (sawOffline) throw offlineError()
    return false
  })()
  try {
    return await refreshInFlight
  } finally {
    // Clear so the NEXT expiry (an hour later) starts a fresh refresh.
    refreshInFlight = null
  }
}

// In-flight GET dedupe. When two unrelated callers (e.g. two providers'
// useEffects) both fire the same GET /profile within the same tick, we
// want them to share ONE network request instead of issuing duplicate
// roundtrips that the backend would have to grade identically anyway.
// Keyed by method + URL + language + id token (since localized error
// messages depend on Accept-Language, and two accounts must not share a
// /profile response across a logout/login race). The promise is dropped
// from the map once it settles, so
// subsequent calls retry fresh — this is dedupe-of-concurrent, not a
// long-lived cache.
//
// Mutating verbs (POST/PUT/PATCH/DELETE) are NEVER deduped — coalescing
// two writes would silently drop one user intent.
const inFlightGets = new Map<string, Promise<unknown>>()

export function clearInFlightGets(): void {
  inFlightGets.clear()
}

// Generic request function with token refresh.
//
// Thin wrapper around doRequest that adds GET-only in-flight dedupe.
// See the inFlightGets comment above for the contract.
async function request<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const method = (options.method || 'GET').toUpperCase()
  // GETs that carry an AbortSignal manage their own request lifecycle
  // (abort-on-unmount / abort-on-param-change hooks). They must NOT share
  // a promise with other callers: (a) a just-aborted request would poison
  // a synchronous re-issue (StrictMode remount: the map's settle-cleanup
  // is a microtask behind), and (b) two callers sharing one promise share
  // one signal — aborting one would kill the other's request.
  if (method !== 'GET' || options.signal) {
    return doRequest<T>(endpoint, options)
  }
  const key = `GET ${endpoint}\n${i18n.language || 'en'}\n${tokenStorage.getIdToken() || ''}`
  const existing = inFlightGets.get(key)
  if (existing) return existing as Promise<T>
  const promise = doRequest<T>(endpoint, options)
  inFlightGets.set(key, promise)
  // Use the catch-then-return-promise trick instead of `.finally` so a
  // rejection still clears the entry — `.finally` returns a new promise
  // we'd have to await, which would chain a microtask after every settle.
  promise.then(
    () => inFlightGets.delete(key),
    () => inFlightGets.delete(key),
  )
  return promise
}

async function doRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${API_BASE_URL}${endpoint}`

  // Rolling-inactivity gate. For any authenticated endpoint, if more than
  // 24 hours has passed since the last successful activity, treat the
  // session as expired and force re-login — even if Cognito's refresh
  // token would still technically validate. Auth endpoints (`/auth/...`)
  // are exempt because login itself needs to work after long idle gaps.
  if (!endpoint.startsWith('/auth/') && isInactivityExpired()) {
    clearActivity()
    tokenStorage.clearTokens()
    throw expireSession()
  }

  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    // Tell the backend which language to localize error messages in. Header
    // is read by utils.LangFromHeaders on the Lambda side. Accept-Language is
    // a CORS-safelisted-request-header so it never triggers a preflight.
    'Accept-Language': i18n.language || 'en',
    ...(options.headers as Record<string, string>),
  }

  // User's local timezone offset in MINUTES WEST of UTC (matches the
  // semantics of JS Date.prototype.getTimezoneOffset()). Only attached to
  // /progress endpoints because:
  //   1. It's the only path that needs it (daily bucketing).
  //   2. It's a custom header — sending it on every request would force a
  //      CORS preflight against routes whose API Gateway config doesn't
  //      list it in cors_allowed_headers (default: Content-Type,
  //      Authorization, X-Request-ID), breaking the entire app.
  // If we ever need it elsewhere, add the path here AND add the header to
  // the Terraform `cors_allowed_headers` list (modules/api-gateway/variables.tf).
  if (endpoint.startsWith('/progress')) {
    headers['X-Tz-Offset'] = String(new Date().getTimezoneOffset())
  }

  // Inject the id token as a Bearer token for the API Gateway JWT authorizer.
  const idToken = tokenStorage.getIdToken()
  if (idToken) {
    headers['Authorization'] = `Bearer ${idToken}`
  }

  let response = await fetchOrThrowOffline(url, { ...options, headers })

  // Handle 401 with localStorage-based token refresh (skip for auth endpoints).
  //
  // Crucially, we distinguish two kinds of 401:
  //
  //   1. AUTH 401 — the JWT authorizer rejected the request (expired/missing
  //      token). API Gateway emits these as `{"message":"Unauthorized"}` with
  //      NO `code` field. These are recoverable via /auth/refresh.
  //
  //   2. BUSINESS 401 — a Lambda handler returned an AppError with
  //      StatusCode=401 (e.g. a handler rejecting a stale or invalid request).
  //      These carry our
  //      structured `{ error, code }` shape and the `code` field is present.
  //      Trying to refresh is useless here — the retry will return the same
  //      business 401 — and worse, if the refresh attempt itself fails for
  //      unrelated reasons (transient network, expired refresh token, etc.)
  //      we'd bounce the user to /auth even though their session is fine
  //      and they just need to retry with different inputs.
  //
  // So: peek at the body. If it has a `code`, treat as business 401 and let
  // it flow through to the caller's catch. Only auth-shape 401s trigger the
  // refresh dance.
  if (response.status === 401 && !endpoint.startsWith('/auth/')) {
    // Clone so the body can still be consumed by the !response.ok branch
    // below if we decide this is a business 401 and skip refresh.
    const peek = response.clone()
    let businessCode: string | null = null
    try {
      const peekJson = await peek.json()
      if (peekJson && typeof peekJson.code === 'string' && peekJson.code) {
        businessCode = peekJson.code
      }
    } catch {
      // No JSON body or unparseable — treat as auth 401, fall through to refresh.
    }

    if (!businessCode) {
      // Coalesced refresh: concurrent 401s share ONE /auth/refresh round-trip.
      // Only a real failure (no/expired refresh token, bad response) expires
      // the session — a recoverable hourly expiry is refreshed transparently.
      const refreshed = await refreshSession()
      if (!refreshed) {
        throw expireSession()
      }
      headers['Authorization'] = `Bearer ${tokenStorage.getIdToken()}`
      response = await fetchOrThrowOffline(url, { ...options, headers })
    }
  }

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({
      error: 'An error occurred',
    }))
    // Prefer the structured `code` from the backend (stable across translations);
    // fall back to a generic API_ERROR.
    const code = typeof errorData.code === 'string' && errorData.code
      ? errorData.code
      : 'API_ERROR'
    // For the human-readable message, prefer a client-side translation keyed
    // on the code (so we get the correct language even when an older backend
    // hasn't translated this error yet). Falls through to the backend message.
    const clientKey = `errors.${code}`
    const clientTranslation = i18n.exists(clientKey) ? i18n.t(clientKey) : null
    const message = clientTranslation || errorData.error || 'Request failed'
    throw new ApiError(code, message, response.status)
  }

  // Successful authenticated response → reset the inactivity clock. Auth
  // endpoints are deliberately excluded so the gate doesn't get reset by
  // anonymous login attempts (a logged-out browser hitting /auth/login
  // shouldn't establish a "session" for the purposes of inactivity).
  if (!endpoint.startsWith('/auth/')) {
    recordActivity()
  }

  // Parse via text-then-JSON instead of `response.json()` directly because
  // API Gateway strips the body on 204 No Content responses (DELETE handlers
  // return 204 with `body: nil`, which serializes as the string "null"
  // server-side but reaches the client as an empty body once CloudFront /
  // API Gateway enforce the no-body rule). `Response.json()` on an empty
  // body throws "Unexpected end of JSON input" — caller code shouldn't
  // need to know which endpoints return 204 to defend against that.
  const text = await response.text()
  if (!text) return undefined as T
  return JSON.parse(text) as T
}

// Auth API (Cognito OTP flow via backend)
export const authApi = {
  async register(data: RegisterRequest): Promise<AuthResponse> {
    return request<AuthResponse>('/auth/register', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async login(data: LoginRequest): Promise<AuthResponse> {
    return request<AuthResponse>('/auth/login', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async verifyOTP(data: VerifyOTPRequest): Promise<AuthResponse> {
    const endpoint = data.method === 'phone' ? '/auth/verify-phone' : '/auth/verify'
    return request<AuthResponse>(endpoint, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    return request<AuthResponse>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
    })
  },

  async logout(): Promise<void> {
    try {
      // Send the current access token so the backend can call Cognito's
      // GlobalSignOut and revoke server-side. Without this body, the
      // handler bails at the empty-token check and the access token stays
      // valid for its full natural TTL (~1 hour) even though the client
      // has cleared local storage — a real session-fixation risk if the
      // token leaked. Best-effort: if no token is in storage (anonymous
      // tab calling logout for some reason) we still hit the endpoint
      // with an empty body so the server logs the attempt.
      const token = tokenStorage.getToken() ?? ''
      await request<void>('/auth/logout', {
        method: 'POST',
        body: JSON.stringify({ accessToken: token }),
      })
    } catch {
      // Server-side revocation is best-effort.
    }
  },

}

// Profile API
export const profileApi = {
  async getProfile(): Promise<User> {
    return request<User>('/profile')
  },

  async updateProfile(data: Partial<User>): Promise<User> {
    return request<User>('/profile', {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  // Sparse merge — backend flips only the keys present in this map. Use for
  // dismissing or marking a specific onboarding without touching others.
  async markOnboardingCompleted(key: string): Promise<User> {
    return request<User>('/profile', {
      method: 'PUT',
      body: JSON.stringify({ onboardingCompleted: { [key]: true } }),
    })
  },
}

// Bookmarks API
export const bookmarksApi = {
  async create(data: {
    surahNumber: number
    ayahNumber: number
    note?: string
    tags?: string[]
  }): Promise<Bookmark> {
    return request<Bookmark>('/bookmarks', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async list(limit = 50): Promise<Bookmark[]> {
    return request<Bookmark[]>(`/bookmarks?limit=${limit}`)
  },

  async update(
    id: string,
    data: { note?: string; tags?: string[] }
  ): Promise<Bookmark> {
    return request<Bookmark>(`/bookmarks/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async delete(id: string): Promise<void> {
    return request<void>(`/bookmarks/${id}`, {
      method: 'DELETE',
    })
  },
}

// Progress API
export const progressApi = {
  /** Record progress for an ayah. `durationSec` is the wall-clock audio
   *  seconds the user spent on this ayah; drives the Progress page's
   *  "Total Minutes" stat. Required — pass 0 only when truly unknown
   *  (the verse will still be counted, just no listening time). */
  async record(surahNumber: number, ayahNumber: number, durationSec: number): Promise<void> {
    return request<void>('/progress/record', {
      method: 'POST',
      body: JSON.stringify({ surahNumber, ayahNumber, durationSec }),
    })
  },

  async getToday(): Promise<ReadingProgress> {
    return request<ReadingProgress>('/progress')
  },

  async getHistory(days = 30, signal?: AbortSignal): Promise<ReadingProgress[]> {
    return request<ReadingProgress[]>(`/progress/history?days=${days}`, { signal })
  },
}

// Memorization API
export const memorizationApi = {
  async addVerse(
    surahNumber: number,
    ayahNumber: number
  ): Promise<MemorizationData> {
    return request<MemorizationData>('/memorization/verses', {
      method: 'POST',
      body: JSON.stringify({ surahNumber, ayahNumber }),
    })
  },

  async listVerses(
    status?: string,
    limit = 50
  ): Promise<MemorizationData[]> {
    const params = new URLSearchParams()
    if (status) params.append('status', status)
    params.append('limit', String(limit))
    return request<MemorizationData[]>(`/memorization/verses?${params}`)
  },

  /**
   * Remove a verse from the memorization list. Idempotent on the server —
   * 204 whether or not the row existed. Caller can drop the entry from
   * local state optimistically and roll back only on a non-204 error.
   */
  async removeVerse(surahNumber: number, ayahNumber: number): Promise<void> {
    await request<void>(`/memorization/verses/${surahNumber}/${ayahNumber}`, {
      method: 'DELETE',
    })
  },

  async startTest(
    testType: string,
    verseCount: number
  ): Promise<MemorizationTest> {
    return request<MemorizationTest>('/memorization/tests', {
      method: 'POST',
      body: JSON.stringify({ testType, verseCount }),
    })
  },

  async submitTest(
    testId: string,
    answers: TestVerseAttempt[],
    duration: number
  ): Promise<MemorizationTest> {
    return request<MemorizationTest>(`/memorization/tests/${testId}/submit`, {
      method: 'POST',
      body: JSON.stringify({ answers, duration }),
    })
  },

  async getStats(): Promise<Record<string, unknown>> {
    return request<Record<string, unknown>>('/memorization/stats')
  },
}

// Notes API
export const notesApi = {
  async create(data: {
    surahNumber: number
    ayahNumber: number
    content: string
    tags?: string[]
  }): Promise<Note> {
    return request<Note>('/notes', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async list(surahNumber?: number, limit = 50): Promise<Note[]> {
    const params = new URLSearchParams()
    if (surahNumber) params.append('surahNumber', String(surahNumber))
    params.append('limit', String(limit))
    return request<Note[]>(`/notes?${params}`)
  },

  async update(
    id: string,
    data: { content?: string; tags?: string[] }
  ): Promise<Note> {
    return request<Note>(`/notes/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async delete(id: string): Promise<void> {
    return request<void>(`/notes/${id}`, {
      method: 'DELETE',
    })
  },
}

// Games API — records one game session against the unified learning stats.
// "Source" is informational only on the wire today (used for analytics later);
// the backend only mutates totals/XP/streak. Best-effort: callers should
// render the result UI even if this call fails.
export const gamesApi = {
  async recordSession(data: {
    gameType: GameType
    source: GameSource
    totalQuestions: number
    correctAnswers: number
    durationSec: number
  }): Promise<RecordGameSessionResponse> {
    return request<RecordGameSessionResponse>('/games/sessions', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // List the user's most recent game sessions (default 5, max 25). Feeds
  // the dashboard's Recent Activity and the per-launcher "N this week"
  // badges. Backend caps `limit` server-side too.
  async listRecentSessions(limit = 5): Promise<GameSession[]> {
    const r = await request<{ sessions: GameSession[] }>(
      `/games/sessions?limit=${encodeURIComponent(String(limit))}`,
    )
    return r.sessions ?? []
  },
}

// Feedback API — POST /feedback. Auth is OPTIONAL on the server side: if the
// caller is signed in, the JWT is honored and the userId is attached server-
// side; otherwise the entry is anonymous. We pass userAgent here (instead of
// letting the backend infer from the User-Agent header) so it survives
// API Gateway header normalization quirks.
export const feedbackApi = {
  async send(input: {
    type: 'bug' | 'feature' | 'other'
    message: string
    email?: string
    page?: string
  }): Promise<{ feedbackId: string }> {
    return request<{ feedbackId: string }>('/feedback', {
      method: 'POST',
      body: JSON.stringify({
        ...input,
        userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      }),
    })
  },
}

// Surahs API — POST /surahs/{surahNumber}/listens. Fire-and-forget from the
// playlist player when a surah completes; callers should not block UI on the
// result. The new total is returned for surfaces that want to surface it
// (e.g. inline "Listened Nx" chip without a profile refetch).
export const surahsApi = {
  async recordListen(surahNumber: number): Promise<{ count: number }> {
    return request<{ count: number }>(`/surahs/${surahNumber}/listens`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },
}

// Playlists API
export const playlistsApi = {
  async create(data: CreatePlaylistInput): Promise<Playlist> {
    return request<Playlist>('/playlists', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async list(limit = 50): Promise<{ playlists: Playlist[]; count: number }> {
    return request<{ playlists: Playlist[]; count: number }>(
      `/playlists?limit=${limit}`
    )
  },

  async get(id: string): Promise<Playlist> {
    return request<Playlist>(`/playlists/${id}`)
  },

  async update(id: string, data: UpdatePlaylistInput): Promise<Playlist> {
    return request<Playlist>(`/playlists/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  },

  async delete(id: string): Promise<void> {
    return request<void>(`/playlists/${id}`, {
      method: 'DELETE',
    })
  },
}

// Shared Playlists API — the read-only community directory, share-link
// resolution, and clone-to-my-playlists. All three routes require auth (the
// whole /shared surface stays behind login, per the playlist-sharing design
// doc); `request` attaches the bearer token automatically like every other
// authenticated call here.
export const sharedPlaylistsApi = {
  // `cursor` is the opaque value from a previous page's response; omit for
  // the first page. `limit` mirrors the backend's default (20) — the handler
  // also clamps server-side (cap 50), so an out-of-range value here is never
  // a hard failure.
  async list(
    cursor?: string,
    limit = 20
  ): Promise<{ playlists: SharedPlaylistSummary[]; cursor: string }> {
    const params = new URLSearchParams()
    params.append('limit', String(limit))
    if (cursor) params.append('cursor', cursor)
    return request<{ playlists: SharedPlaylistSummary[]; cursor: string }>(
      `/playlists/shared?${params}`
    )
  },

  // Full playlist (surahs + settingsBySurah + ownerDisplayName), userId
  // omitted by the backend. 404 (surfaced as ApiError) when private, deleted,
  // or never existed — the three are indistinguishable by design.
  async get(playlistId: string): Promise<Playlist> {
    return request<Playlist>(`/playlists/shared/${playlistId}`)
  },

  // Copies the shared playlist into a brand-new playlist owned by the
  // caller (always created private). Rate-limited server-side.
  async clone(playlistId: string): Promise<Playlist> {
    return request<Playlist>(`/playlists/shared/${playlistId}/clone`, {
      method: 'POST',
      body: JSON.stringify({}),
    })
  },
}

// Stats API — public, unauthenticated. The `request` helper attaches a JWT
// when one is in storage; the backend ignores it for this route so passing
// it through is harmless.
export interface PopularSurah {
  surahNumber: number
  count: number
}

export interface PopularAyah {
  surahNumber: number
  ayahNumber: number
  count: number
}

export interface PopularContent {
  surahs: PopularSurah[]
  ayahs: PopularAyah[]
}

export const statsApi = {
  /**
   * Fetch both popular surahs (by listen count) and popular ayahs (by
   * bookmark count) in one round-trip. Either sub-list may be empty if its
   * underlying aggregate query fails — the home Discover section hides
   * empty sub-sections.
   */
  async getPopular(surahLimit = 10, ayahLimit = 10): Promise<PopularContent> {
    return request<PopularContent>(
      `/stats/popular?surahs=${surahLimit}&ayahs=${ayahLimit}`,
    )
  },
}

